import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRerender } from '@elfsight/widget-utils';
import equal from 'fast-deep-equal';
import type { Collector } from './createCollector';
import { createCollector } from './createCollector';
import type { AggregateFetcherResult } from './resultCreator';
import { createAllGroupResults } from './resultCreator';
import { createSourceGroups } from './sourceGrouping';
import type {
  AggregateCollectorStatus,
  AggregateFetcherSource,
  OrderFunction
} from './types';

interface State {
  status: AggregateCollectorStatus;
  data: unknown[][];
  redundantData: unknown[][];
}

interface AggregateFetcherConfig {
  sliceLength: number;
  order?: OrderFunction<any>;
  limit?: number;
  groupBySourceType?: boolean;
}

export function useAggregateFetcher(
  // @todo find the way to infer types for each source
  // "any/unknown" sucks
  sources: AggregateFetcherSource<any, any, any>[],
  config: AggregateFetcherConfig
): AggregateFetcherResult[] {
  const sourcesRef = useRef(sources);
  const configRef = useRef(config);
  const collectorsRef = useRef<
    Collector<
      Parameters<(typeof sources)[number]['select']>[0],
      ReturnType<(typeof sources)[number]['select']>
    >[]
  >([]);

  const fetchingRef = useRef<boolean[]>(sources.map(() => false));

  // Create default empty data arrays for each source
  const defaultData = useMemo(
    () => ({
      data: sources.map(() => []),
      redundantData: sources.map(() => [])
    }),
    [sources.length]
  );

  // compound the key
  const key = sources.map((source) => source.key);
  const effectiveKeyRef = useRef<unknown[]>([]);
  const revalidationScheduled = !equal(key, effectiveKeyRef.current);

  // having a state in the ref makes it easy
  // to handle the situation when you need an updated state
  // right after the dispatch call
  // inspired by https://github.com/vercel/swr/blob/master/src/use-swr.ts#L249
  // and the the `Shot in the Dark` by Ozzy Osbourne
  const stateRef = useRef<State>({
    status: 'pending',
    ...defaultData
  });

  const rerender = useRerender();
  const dispatch = useCallback(
    (stateUpdate: Partial<State> | ((prevState: State) => Partial<State>)) => {
      if (typeof stateUpdate === 'function') {
        stateRef.current = {
          ...stateRef.current,
          ...stateUpdate(stateRef.current)
        };
      } else {
        stateRef.current = { ...stateRef.current, ...stateUpdate };
      }
      rerender();
    },
    [rerender]
  );

  // A function for getting hasNext for a specific source
  const getSourceHasNext = useCallback(
    (sourceIndex: number) => {
      const sourceCollector = collectorsRef.current[sourceIndex];
      const sourceRedundant = stateRef.current.redundantData[sourceIndex] || [];
      const sourceData = stateRef.current.data[sourceIndex] || [];

      return (
        sourceData.length < (config.limit ?? Infinity) &&
        (sourceCollector?.hasNext || sourceRedundant.length > 0)
      );
    },
    [config.limit]
  );

  // A function to reset a specific source
  const getSourceReset = useCallback(
    (sourceIndex: number) => () => {
      dispatch((prevState) => {
        const { sliceLength } = configRef.current;
        const { data, redundantData } = prevState;

        // Resetting the data from a specific source to sliceLength
        const nextData = [...data];
        const nextRedundantData = [...redundantData];

        const sourceData = data[sourceIndex] || [];
        const keptData = sourceData.slice(0, sliceLength);
        const excessData = sourceData.slice(sliceLength);

        nextData[sourceIndex] = keptData;
        nextRedundantData[sourceIndex] = [];

        // Returning excess data back to the collector
        const collector = collectorsRef.current[sourceIndex];
        if (collector && excessData.length > 0) {
          collector.rollback(excessData as any[]);
        }

        return {
          status: 'success',
          data: nextData,
          redundantData: nextRedundantData
        };
      });
    },
    [dispatch]
  );

  // A function for fetching the following data from a specific source
  // Each source has its own next function
  const getSourceNext = useCallback(
    (sourceIndex: number) => async () => {
      const canFetchNext =
        !fetchingRef.current[sourceIndex] && getSourceHasNext(sourceIndex);

      if (!canFetchNext) {
        return;
      }

      // Capture the key at the start of the request to check for race conditions
      const requestKey = effectiveKeyRef.current;

      dispatch({ status: 'pending' });
      fetchingRef.current[sourceIndex] = true;
      const sourceCollector = collectorsRef.current[sourceIndex];

      if (!sourceCollector?.hasNext) {
        // If the collector does not have hasNext, just return the status to success
        dispatch({ status: 'success' });
        fetchingRef.current[sourceIndex] = false;
        return;
      }

      try {
        const { sliceLength, order } = configRef.current;
        const result = await sourceCollector.getNext(sliceLength);

        // Check if the key changed during the request (race condition protection)
        if (requestKey !== effectiveKeyRef.current) {
          fetchingRef.current[sourceIndex] = false;
          return;
        }

        dispatch((prevState) => {
          const { data, redundantData } = prevState;
          const existingSourceData = data[sourceIndex] || [];
          const currentRedundant = redundantData[sourceIndex] || [];

          let sourceData = result.data.concat(currentRedundant);

          if (order) {
            sourceData = order(sourceData);
          }

          const neededData = sourceData.slice(0, sliceLength);
          const nextSourceRedundant = sourceData.slice(sliceLength);

          // rollback redundant to collector
          if (nextSourceRedundant.length > 0) {
            sourceCollector.rollback(nextSourceRedundant);
          }

          // update data only for this specific source
          const nextData = [...data];
          const nextRedundantData = [...redundantData];

          nextData[sourceIndex] = existingSourceData.concat(neededData);
          nextRedundantData[sourceIndex] = nextSourceRedundant;

          return {
            status: 'success',
            data: nextData,
            redundantData: nextRedundantData
          };
        });
      } catch (_error) {
        // In case of an error, we return the status back to success
        dispatch({ status: 'success' });
      } finally {
        fetchingRef.current[sourceIndex] = false;
      }
    },
    [getSourceHasNext, dispatch]
  );

  const collect = useCallback(async () => {
    const { current: intrinsicKey } = effectiveKeyRef;
    const { sliceLength, order } = configRef.current;

    // get results from all collectors (not just those with hasNext)
    const results = await Promise.all(
      collectorsRef.current.map((collector) => {
        if (collector.hasNext) {
          return collector.getNext(sliceLength);
        }

        // return an empty result for collectors without hasNext
        return Promise.resolve({ data: [] });
      })
    );

    // check whether the effective and the request keys match
    // if the rule is violated it means that result is obsolete
    // and we should ignore it
    if (intrinsicKey !== effectiveKeyRef.current) {
      return;
    }

    dispatch((prevState) => {
      const { redundantData, data } = prevState;

      // prepare separated data for each source
      const nextData = results.map((result: any, index: number) => {
        const existingSourceData = data[index] || [];

        if (!result.data.length) {
          // If there is no new data, we return the existing ones
          return existingSourceData;
        }

        const currentRedundant = redundantData[index] || [];
        let sourceData = result.data.concat(currentRedundant);

        // apply order to individual source data
        if (order) {
          sourceData = order(sourceData);
        }

        // take needed amount and prepare redundant for this source
        const neededData = sourceData.slice(0, sliceLength);
        const nextSourceRedundant = sourceData.slice(sliceLength);

        // rollback redundant to collector for this source
        if (collectorsRef.current[index] && nextSourceRedundant.length > 0) {
          collectorsRef.current[index].rollback(nextSourceRedundant);
        }

        return existingSourceData.concat(neededData);
      });

      // prepare redundant data for each source
      const nextRedundantData = results.map((result: any, index: number) => {
        if (!result.data.length) {
          // If there is no new data, we return the existing redundant data
          return redundantData[index] || [];
        }

        const currentRedundant = redundantData[index] || [];
        let sourceData = result.data.concat(currentRedundant);

        if (order) {
          sourceData = order(sourceData);
        }

        return sourceData.slice(sliceLength);
      });

      return {
        status: 'success',
        data: nextData,
        redundantData: nextRedundantData
      };
    });
  }, [dispatch]);

  useEffect(() => {
    sourcesRef.current = sources;
    configRef.current = config;
  });

  useEffect(() => {
    if (revalidationScheduled) {
      effectiveKeyRef.current = key;
      collectorsRef.current = sourcesRef.current.map((source) =>
        createCollector({
          page: source.page,
          select: source.select,
          order: configRef.current.order,
          fetch: source.fetch,
          filter: source.filter,
          link: source.link
        })
      );

      dispatch({
        status: 'pending',
        ...defaultData
      });

      collect();
    }
  }, [revalidationScheduled, key, dispatch, collect]);

  const { groupBySourceType } = configRef.current;
  // Create source groups
  const sourceGroups = createSourceGroups(sources, groupBySourceType);

  // invalid key invalidates the state also
  // so we need to return the pending result
  // instead of the current one
  if (revalidationScheduled) {
    const pendingResults: AggregateFetcherResult[] = sourceGroups.map(
      (group) => ({
        id: group.groupId,
        status: 'pending' as const,
        data: [],
        hasNext: true,
        next: () => Promise.resolve(),
        reset: () => {},
        sourcesStates: []
      })
    );

    return pendingResults;
  }

  // Create results for all groups using the universal mechanism
  const results = createAllGroupResults(sourceGroups, {
    data: stateRef.current.data,
    status: stateRef.current.status,
    collectors: collectorsRef.current,
    limit: config.limit,
    order: config.order,
    getSourceHasNext,
    getSourceNext,
    getSourceReset
  });

  return results;
}
